/**
 * @fileoverview Temporary ("ghost") molecules: `MakeTempMolecule` / `UpdateTempMolecule` /
 * `RemoveTempMolecule`.
 *
 * The hot path is the fragment pose cloud of ui/dialog/FragmentSearch/LigandMod.jsx: hovering a
 * row creates up to a few hundred poses of ONE fragment (same atoms, other coordinates), 100 per
 * animation frame with a redisplay after each chunk, and leaving the row removes them all.
 * Selecting a pose changes the opacity of two of them.
 *
 * So molecules with the same topology, style and colours share ONE multi-state PyMOL object (a
 * "pool", `_bm_tmp<n>`, `all_states` on): the first molecule is loaded as CIF, every further one
 * is one `load_coordset` that appends a state (0.04 ms per call, VERIFIED on payload 0.1.0 -
 * the whole cloud costs one CIF parse, and PyMOL draws it as one object). Opacity: the most
 * common value of a pool is ONE object-level setting (`stick_transparency` ...); a molecule
 * with another opacity gets a PER-STATE setting (the same setting with a state argument,
 * VERIFIED to read back per state; 0.1-0.4 ms per call), so selecting a pose costs one or two
 * calls instead of one per pose. A removed molecule leaves a hole:
 * its state is made fully transparent and reused by the next molecule of the pool; when holes
 * outnumber the live states, or the style has no transparency setting (lines), the pool is
 * rebuilt compactly at the next flush. An empty pool is deleted.
 *
 * Temporary molecules are hoverable (3Dmol: hoverable, not clickable): `hitTest()` finds the
 * atom under the pointer in JavaScript (their coordinates are known here), and the target it
 * returns maps back to the pose's own BMaps atom through `visAtom.appAtom`. They never enter
 * the main atom maps.
 */

import { guarded } from './PyMOLGuard';
import {
    ObjectPrefix, ObjectKinds, RepTransparencySetting, getStyleSpec, repMask,
} from './PyMOLConstants';
import { MolStyles } from '../style_manager';
import { cifFromAtoms } from './PyMOLCifWriter';
import { sameView, pointSegment2D } from './PyMOLProjection';

/** Hit radius around a temporary atom (CSS px), as the wrapper's hover for real atoms. */
const HoverRadiusPx = 6;
/** Half of each bond belongs to its atom, as 3Dmol's stick intersection shapes (Angstrom). */
const StickRadius = 0.25;
/** Nominal atom radius for depth comparisons with other hover targets (Angstrom). */
const AtomFrontOffset = 0.5;
/** Holes tolerated before a pool is rebuilt compactly: max(this, live states). */
const MaxHoles = 16;

function bondsOf(atom) {
    if (typeof atom.getBonds === 'function') {
        return atom.getBonds().map(
            (bond) => [bond.atom1 === atom ? bond.atom2 : bond.atom1, bond.order],
        );
    }
    const partners = atom.bondedAtoms || [];
    return partners.map((other, i) => [other, (atom.bondOrder || [])[i] || 1]);
}

/** Bonds inside an atom list, as [i, j, order] with i < j (list indexes). */
export function bondsWithin(atoms) {
    const index = new Map(atoms.map((atom, i) => [atom, i]));
    const bonds = [];
    atoms.forEach((atom, i) => {
        for (const [other, order] of bondsOf(atom)) {
            const j = index.get(other);
            if (j !== undefined && j > i) bonds.push([i, j, order]);
        }
    });
    return bonds;
}

/** Elements + bond graph of an atom list (in list order): molecules that share it share a pool. */
export function topologySignature(atoms, bonds=bondsWithin(atoms)) {
    const graph = bonds.map(([i, j, order]) => `${i}-${j}:${order}`).sort().join(',');
    return `${atoms.map((a) => a.elem).join(',')}|${graph}`;
}

function newStats() {
    return {
        loaded: 0, deleted: 0, appended: 0, replaced: 0, stateSettings: 0, errors: 0,
    };
}

export class PyMOLTempMolecules {
    /**
     * @param {object} options
     * @param {(n: number) => Int32Array} options.allocateIds n fresh atom ids
     */
    constructor({ allocateIds }) {
        this.allocateIds = allocateIds;
        /** @type {Map<object, object>} handle -> record */
        this.records = new Map();
        /** @type {Map<string, object>} pool key -> pool */
        this.pools = new Map();
        this.poolSeq = 0;
        this.repOrder = undefined;
        this.hitCache = null;
        this.lastStats = newStats();
    }

    get size() { return this.records.size; }

    setRepOrder(order) { this.repOrder = order; }

    /**
     * @param {object} handle what MakeTempMolecule returned to the app
     * @param {object[]} atoms BMaps atoms (with finite coordinates)
     * @param {object} spec
     * @param {string} spec.style a MolStyles code
     * @param {Uint32Array} spec.colors 0xRRGGBB per atom
     * @param {number} spec.opacity 0..1
     */
    add(handle, atoms, { style, colors, opacity }) {
        const xyz = new Float32Array(atoms.length * 3);
        atoms.forEach((atom, i) => xyz.set(atom.getPosition(), i * 3));
        const bonds = bondsWithin(atoms);
        const record = {
            handle,
            atoms,
            xyz,
            bonds,
            colors,
            style: getStyleSpec(style) ? style : MolStyles.sticks,
            opacity,
            topology: topologySignature(atoms, bonds),
            pool: null,
            state: 0,
            hoverTargets: null,
        };
        this.records.set(handle, record);
        if (atoms.length > 0) this.place(record);
        this.hitCache = null;
        return record;
    }

    /** New style / colours move the molecule to another pool; opacity is a per-state change. */
    update(handle, { style, opacity, colors }) {
        const record = this.records.get(handle);
        if (!record) return;
        let move = false;
        if (style !== undefined) {
            const newStyle = getStyleSpec(style) ? style : MolStyles.sticks;
            if (newStyle !== record.style) {
                record.style = newStyle;
                move = true;
            }
        }
        if (colors && colors.join(',') !== record.colors.join(',')) {
            record.colors = colors;
            move = true;
        }
        if (opacity !== undefined && opacity !== record.opacity) {
            record.opacity = opacity;
            if (!move && record.pool) record.pool.dirtyStates.add(record.state);
        }
        if (move && record.pool) {
            this.unplace(record);
            this.place(record);
            this.hitCache = null;
        }
    }

    remove(handle) {
        const record = this.records.get(handle);
        if (!record) return false;
        this.records.delete(handle);
        if (record.pool) this.unplace(record);
        this.hitCache = null;
        return true;
    }

    isPoolName(name) {
        for (const pool of this.pools.values()) if (pool.name === name) return true;
        return false;
    }

    // ------------------------------------------------------------------ pools

    poolKey(record) {
        return `${record.topology}|${record.style}|${record.colors.join(',')}`;
    }

    place(record) {
        const key = this.poolKey(record);
        let pool = this.pools.get(key);
        if (!pool) {
            this.poolSeq++;
            const spec = getStyleSpec(record.style);
            pool = {
                key,
                name: `${ObjectPrefix}${ObjectKinds.temp}${this.poolSeq}`,
                style: record.style,
                ids: this.allocateIds(record.atoms.length),
                colors: record.colors,
                /** state - 1 -> record | null (a hole) */
                slots: [],
                holes: [],
                ops: [],
                dirtyStates: new Set(),
                /** object-level transparency (the pool's most common value) */
                base: 0,
                /** state - 1 -> per-state transparency PyMOL was last told (undefined = none) */
                pushed: [],
                transparencySettings: [
                    ...new Set(spec.reps.map((rep) => RepTransparencySetting[rep]).filter(Boolean)),
                ],
                hidesByTransparency: spec.reps.every((rep) => RepTransparencySetting[rep]),
                // 3Dmol's clicksphere draws nothing: every state stays fully transparent
                invisible: record.style === MolStyles.invisible,
                loaded: false,
                rebuild: false,
            };
            this.pools.set(key, pool);
        }
        record.pool = pool;
        if (pool.loaded && !pool.rebuild && pool.holes.length > 0) {
            record.state = pool.holes.pop();
            pool.slots[record.state - 1] = record;
            pool.ops.push({ type: 'replace', state: record.state });
        } else {
            pool.slots.push(record);
            record.state = pool.slots.length;
            if (pool.loaded && !pool.rebuild) pool.ops.push({ type: 'append', state: record.state });
        }
        pool.dirtyStates.add(record.state);
    }

    unplace(record) {
        const { pool } = record;
        record.pool = null;
        if (!pool) return;
        pool.slots[record.state - 1] = null;
        if (pool.loaded && !pool.rebuild) {
            pool.holes.push(record.state);
            pool.dirtyStates.add(record.state);
            const live = pool.slots.filter(Boolean).length;
            if (!pool.hidesByTransparency || pool.holes.length > Math.max(MaxHoles, live)) {
                pool.rebuild = true;
            }
        }
        record.state = 0;
    }

    // ------------------------------------------------------------------ flush

    flush(viewer) {
        const stats = newStats();
        this.lastStats = stats;
        for (const [key, pool] of [...this.pools]) {
            const live = pool.slots.filter(Boolean);
            if (live.length === 0) {
                if (pool.loaded) {
                    this.guard(stats, `delete ${pool.name}`, () => { viewer.delete(pool.name); stats.deleted++; });
                }
                this.pools.delete(key);
                continue;
            }
            if (!pool.loaded || pool.rebuild) {
                this.guard(stats, `load ${pool.name}`, () => this.build(viewer, pool, live, stats));
            } else {
                this.guard(stats, `update ${pool.name}`, () => this.update1(viewer, pool, stats));
            }
        }
        return stats;
    }

    guard(stats, what, fn) {
        return guarded(stats, `temporary molecules: ${what}`, fn);
    }

    build(viewer, pool, live, stats) {
        pool.rebuild = false;
        pool.ops = [];
        pool.holes = [];
        pool.dirtyStates.clear();
        pool.pushed = [];
        pool.base = 0;
        pool.slots = live;
        live.forEach((record, i) => { record.state = i + 1; });
        if (pool.loaded) {
            viewer.delete(pool.name);
            pool.loaded = false;
        }
        const [template] = live;
        const index = new Map(template.atoms.map((atom, i) => [atom, i]));
        const cif = cifFromAtoms(template.atoms, {
            idOf: (atom) => pool.ids[index.get(atom)],
            // state 1 from the positions captured at add(), like every other state and the
            // hover hit-test: a molecule whose atoms moved since is drawn where it was added
            positionOf: (atom) => {
                const i = index.get(atom);
                return template.xyz.subarray(3 * i, 3 * i + 3);
            },
            name: pool.name,
            ssOf: () => 'L',
            oneModel: true,
        });
        viewer.bridge.load_cif(pool.name, cif.text, 0);
        pool.loaded = true;
        for (const record of live.slice(1)) {
            viewer.bridge.set_state_coords(pool.name, 0, record.xyz, 'coordset');
        }
        const spec = getStyleSpec(pool.style);
        viewer.bridge.apply_reps(pool.name, pool.ids, repMask(spec.reps, this.repOrder), 0);
        // the style's own transparency is replaced by the molecule's opacity (3Dmol does the same)
        for (const [name, value] of Object.entries(spec.settings || {})) {
            if (!pool.transparencySettings.includes(name)) viewer.cmd.set(name, value, pool.name);
        }
        if (spec.cartoonType) viewer.cmd.cartoon(spec.cartoonType, pool.name);
        viewer.bridge.apply_colors(pool.name, pool.ids, pool.colors);
        viewer.cmd.set('all_states', 1, pool.name);
        // the most common transparency once for the object, the others per state
        const counts = new Map();
        for (const record of live) {
            const value = this.transparencyOf(pool, record);
            counts.set(value, (counts.get(value) || 0) + 1);
        }
        pool.base = [...counts].sort((a, b) => b[1] - a[1])[0][0];
        if (pool.base !== 0) {
            for (const setting of pool.transparencySettings) {
                viewer.cmd.set(setting, pool.base, pool.name);
            }
        }
        live.forEach((record) => this.pushState(viewer, pool, record.state, stats));
        stats.loaded++;
    }

    update1(viewer, pool, stats) {
        for (const op of pool.ops) {
            const record = pool.slots[op.state - 1];
            // a hole's coordinates do not matter, but an append must still create its state
            const xyz = record ? record.xyz : pool.slots.find(Boolean).xyz;
            viewer.bridge.set_state_coords(pool.name, op.type === 'append' ? 0 : op.state, xyz, 'coordset');
            stats[op.type === 'append' ? 'appended' : 'replaced']++;
        }
        pool.ops = [];
        for (const state of pool.dirtyStates) this.pushState(viewer, pool, state, stats);
        pool.dirtyStates.clear();
    }

    /** Transparency of a live molecule (1 - opacity), 1 for a hole. */
    transparencyOf(pool, record) {
        return record && !pool.invisible ? Math.max(0, Math.min(1, 1 - record.opacity)) : 1;
    }

    /** Per-state transparency, when it differs from what the state shows now. */
    pushState(viewer, pool, state, stats) {
        const value = this.transparencyOf(pool, pool.slots[state - 1]);
        const shown = pool.pushed[state - 1] === undefined ? pool.base : pool.pushed[state - 1];
        if (value === shown) return;
        for (const setting of pool.transparencySettings) {
            viewer.cmd.set(setting, value, pool.name, state);
            stats.stateSettings++;
        }
        pool.pushed[state - 1] = value;
    }

    // ------------------------------------------------------------------ hover

    /**
     * The temporary atom under (x, y) CSS px, front-most: within a few pixels of its centre, or
     * on the half of a bond next to it (a stick).
     * @returns {{target: object, front: number}|null} target = {appAtom, tempMolecule}
     */
    hitTest(projection, x, y) {
        if (!projection || this.records.size === 0) return null;
        const list = this.projected(projection);
        const r2 = HoverRadiusPx * HoverRadiusPx;
        let best = null;
        const consider = (record, i, front) => {
            if (!best || front < best.front) best = { record, i, front };
        };
        for (const { record, screen } of list) {
            for (let i = 0; i < record.atoms.length; i++) {
                const depth = screen[3 * i + 2];
                if (depth <= 0) continue;
                const dx = screen[3 * i] - x;
                const dy = screen[3 * i + 1] - y;
                if (dx * dx + dy * dy <= r2) consider(record, i, depth - AtomFrontOffset);
            }
            for (const [i, j] of record.bonds) {
                const di = screen[3 * i + 2];
                const dj = screen[3 * j + 2];
                if (di <= 0 || dj <= 0) continue;
                const { dist, t } = pointSegment2D(x, y, screen[3 * i], screen[3 * i + 1],
                    screen[3 * j], screen[3 * j + 1]);
                const scale = record.scales[i] + (record.scales[j] - record.scales[i]) * t;
                if (dist > Math.max(2, StickRadius * scale)) continue;
                consider(record, t < 0.5 ? i : j, di + (dj - di) * t - StickRadius);
            }
        }
        if (!best) return null;
        const { record } = best;
        if (!record.hoverTargets) {
            record.hoverTargets = record.atoms.map(
                (appAtom) => ({ appAtom, tempMolecule: record.handle }),
            );
        }
        return { target: record.hoverTargets[best.i], front: best.front };
    }

    projected({
        view, width, height, project,
    }) {
        const key = [...view, width, height];
        if (this.hitCache && sameView(this.hitCache.key, key)) return this.hitCache.list;
        const list = [];
        for (const record of this.records.values()) {
            if (!record.pool) continue;
            const screen = new Float32Array(record.atoms.length * 3);
            record.scales = new Float32Array(record.atoms.length);
            for (let i = 0; i < record.atoms.length; i++) {
                const p = project(record.xyz[3 * i], record.xyz[3 * i + 1], record.xyz[3 * i + 2]);
                screen[3 * i] = p.x;
                screen[3 * i + 1] = p.y;
                screen[3 * i + 2] = p.depth;
                record.scales[i] = p.scale;
            }
            list.push({ record, screen });
        }
        this.hitCache = { key, list };
        return list;
    }
}
